import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import {
  pushSalesOrdersToDemandApi,
  listBatchesApi,
  type DemandBatch,
} from '@/services/demand'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import type { GridLineItem } from './salesOrderGridUtils'

export interface PushToDemandModalProps {
  isOpen: boolean
  line: GridLineItem | null
  orderCode?: string
  onClose: () => void
  onSuccess: (batchId: string) => void
}

export function PushToDemandModal({
  isOpen,
  line,
  orderCode,
  onClose,
  onSuccess,
}: PushToDemandModalProps): React.ReactElement | null {
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [newTitle, setNewTitle] = useState('')
  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [batches, setBatches] = useState<DemandBatch[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorObj, setErrorObj] = useState<any>(null)

  useEffect(() => {
    if (!isOpen || !line) return

    setMode('new')
    setNewTitle(`Đợt nhu cầu SO ${orderCode || ''} - ${line.variant_code || ''}`.trim())
    setSelectedBatchId('')
    setErrorObj(null)

    // Load available open batches
    setLoading(true)
    listBatchesApi()
      .then((res) => {
        setBatches(res)
      })
      .catch(() => {
        // Ignore failure to load batches
      })
      .finally(() => setLoading(false))
  }, [isOpen, line, orderCode])

  if (!isOpen || !line) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!line.id) {
      setErrorObj({ message: 'Dòng đơn hàng chưa được lưu vào hệ thống. Vui lòng bấm Lưu trước.' })
      return
    }

    if (mode === 'new' && !newTitle.trim()) {
      setErrorObj({ message: 'Vui lòng nhập tên đợt nhu cầu mới.' })
      return
    }

    if (mode === 'existing' && !selectedBatchId) {
      setErrorObj({ message: 'Vui lòng chọn một đợt nhu cầu có sẵn.' })
      return
    }

    setSubmitting(true)
    setErrorObj(null)

    try {
      const payload =
        mode === 'new'
          ? {
              target: { mode: 'create_new' as const, title: newTitle.trim() },
              lines: [{ sales_order_line_id: line.id }],
            }
          : {
              target: { mode: 'existing' as const, batch_id: selectedBatchId },
              lines: [{ sales_order_line_id: line.id }],
            }

      const result = await pushSalesOrdersToDemandApi(payload)
      onSuccess(result.batch_id)
      onClose()
    } catch (err: any) {
      setErrorObj(err)
    } finally {
      setSubmitting(false)
    }
  }

  const formattedErr = errorObj ? formatDocumentError(errorObj) : null
  const existingBatchId =
    errorObj?.extra?.existing_batch_id ||
    errorObj?.details?.existing_batch_id ||
    (line.demand_batch_ids && line.demand_batch_ids[0])

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">
            Đưa vào nhu cầu vật tư: {line.variant_code || 'Dòng đơn'}
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 text-sm space-y-4">
            {formattedErr && (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-rose-800 text-xs font-medium space-y-1">
                <p>⚠️ {formattedErr.message}</p>
                {existingBatchId && (
                  <p>
                    <Link
                      to={`/demand`}
                      className="font-semibold text-rose-900 underline hover:text-rose-950"
                      target="_blank"
                    >
                      → Mở phân hệ Nhu cầu để xem đợt liên kết
                    </Link>
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block font-semibold text-slate-700 mb-2">Cách đưa vào:</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="radio"
                    name="pushMode"
                    value="new"
                    checked={mode === 'new'}
                    onChange={() => setMode('new')}
                  />
                  <span>Tạo đợt nhu cầu mới</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="radio"
                    name="pushMode"
                    value="existing"
                    checked={mode === 'existing'}
                    onChange={() => setMode('existing')}
                  />
                  <span>Ghép vào đợt có sẵn</span>
                </label>
              </div>
            </div>

            {mode === 'new' ? (
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">
                  Tên đợt nhu cầu <span className="text-rose-600">*</span>:
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-600"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Ví dụ: Đợt nhu cầu tháng 10..."
                  autoFocus
                />
              </div>
            ) : (
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">
                  Chọn đợt nhu cầu (đang mở) <span className="text-rose-600">*</span>:
                </label>
                {loading ? (
                  <p className="text-xs text-slate-400">Đang tải danh sách đợt...</p>
                ) : batches.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">
                    Không có đợt nhu cầu nào đang mở. Vui lòng chọn &quot;Tạo đợt nhu cầu mới&quot;.
                  </p>
                ) : (
                  <select
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-600"
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                  >
                    <option value="">-- Chọn đợt nhu cầu --</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.title || b.id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={submitting}>
              Hủy
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={submitting}>
              {submitting ? 'Đang xử lý...' : 'Xác nhận đưa vào'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
